
import { useState } from 'react';

interface Mission {
  id: number;
  year: string;
  title: string;
  description: string;
  status: 'completed' | 'ongoing' | 'planned';
  image: string;
  details: string[];
}

export default function MissionTimeline() {
  const [selectedMission, setSelectedMission] = useState<number>(1);

  const missions: Mission[] = [
    {
      id: 1,
      year: '2024',
      title: 'Vyoamyug Foundation & IN-SPACe Registration',
      description: 'Company establishment in Pune with official IN-SPACe registration and core team formation',
      status: 'completed',
      image: 'https://public.readdy.ai/ai/img_res/5e730dc4e9e561cea8202a7a4631e434.jpg',
      details: [
        'Founded by Rahul Vinodkumar Dadhich in Pune, Maharashtra',
        'Successfully registered with IN-SPACe authorization',
        'Assembled core engineering and research team',
        'Established R&D facilities and testing infrastructure'
      ]
    },
    {
      id: 2,
      year: '2025',
      title: 'Yuga-1 Heavy-Lift Launch Vehicle Development',
      description: 'Engineering and testing of our flagship heavy-lift launch vehicle with 40,000 kg LEO payload capacity',
      status: 'ongoing',
      image: 'https://readdy.ai/api/search-image?query=massive%20Indian%20heavy-lift%20rocket%20Yuga-1%20on%20launch%20pad%20with%2040000kg%20payload%20capacity%2C%20advanced%20propulsion%20systems%2C%20professional%20space%20photography%2C%20Indian%20space%20program%20infrastructure&width=600&height=400&seq=mission-2&orientation=landscape',
      details: [
        'Developing 40,000 kg payload capacity to LEO',
        'Advanced hybrid propulsion system integration',
        'AI-powered flight control and navigation systems',
        'Comprehensive ground testing and validation'
      ]
    },
    {
      id: 3,
      year: '2025',
      title: 'Vyoamdristi-1 AI Satellite Launch',
      description: 'First AI-integrated satellite for real-time Earth observation, analytics, and thermal imaging',
      status: 'ongoing',
      image: 'https://readdy.ai/api/search-image?query=advanced%20AI-powered%20satellite%20Vyoamdristi-1%20with%20thermal%20imaging%20and%20Earth%20observation%20sensors%2C%20orbiting%20above%20India%2C%20professional%20space%20photography%2C%20detailed%20satellite%20technology&width=600&height=400&seq=mission-3&orientation=landscape',
      details: [
        'Real-time Earth observation and analytics',
        'Advanced thermal imaging capabilities',
        'AI-powered data processing and transmission',
        'Climate monitoring and disaster prediction systems'
      ]
    },
    {
      id: 4,
      year: '2026',
      title: 'AI Defense Core Integration',
      description: 'Deployment of autonomous defence systems with AI Defense Core for UAVs and aerospace security',
      status: 'planned',
      image: 'https://readdy.ai/api/search-image?query=futuristic%20AI-powered%20defense%20drones%20and%20autonomous%20combat%20systems%2C%20Indian%20military%20aerospace%20technology%2C%20advanced%20UAVs%20with%20artificial%20intelligence%2C%20professional%20defense%20photography&width=600&height=400&seq=mission-4&orientation=landscape',
      details: [
        'AI Defense Core for autonomous combat systems',
        'Advanced UAV integration and control',
        'Stealth technology and adaptive cycles',
        'National security and aerospace supremacy'
      ]
    },
    {
      id: 5,
      year: '2027',
      title: 'Interplanetary Mission Initiative',
      description: 'Launch of advanced propulsion systems for Mars, Moon, and beyond exploration missions',
      status: 'planned',
      image: 'https://readdy.ai/api/search-image?query=Indian%20interplanetary%20spacecraft%20with%20advanced%20propulsion%20heading%20to%20Mars%2C%20red%20planet%20visible%20in%20background%2C%20professional%20space%20exploration%20photography%2C%20cutting-edge%20space%20technology&width=600&height=400&seq=mission-5&orientation=landscape',
      details: [
        'Mars and lunar exploration spacecraft',
        'Advanced propulsion for deep space missions',
        'Autonomous navigation and landing systems',
        'International collaboration for space exploration'
      ]
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'ongoing': return 'bg-blue-500';
      case 'planned': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return 'ri-check-line';
      case 'ongoing': return 'ri-refresh-line';
      case 'planned': return 'ri-time-line';
      default: return 'ri-question-line';
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Our Mission Timeline
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            From founding in 2024 to interplanetary missions, explore Vyoamyug's journey 
            in transforming India's aerospace and defence capabilities
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-500"></div>
            
            <div className="space-y-8">
              {missions.map((mission, index) => (
                <div
                  key={mission.id}
                  className={`relative cursor-pointer transition-all duration-300 ${
                    selectedMission === mission.id ? 'scale-105' : 'hover:scale-102'
                  }`}
                  onClick={() => setSelectedMission(mission.id)}
                >
                  {/* Timeline Node */}
                  <div className={`absolute left-6 w-4 h-4 rounded-full border-4 border-gray-900 ${getStatusColor(mission.status)} z-10`}></div>
                  
                  {/* Content Card */}
                  <div className={`ml-16 p-6 rounded-xl border transition-all duration-300 ${
                    selectedMission === mission.id 
                      ? 'bg-blue-600/20 border-blue-400/50 shadow-lg shadow-blue-500/25' 
                      : 'bg-gray-800/50 border-gray-700 hover:bg-gray-800/70'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-blue-400">{mission.year}</span>
                      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${getStatusColor(mission.status)} text-white`}>
                        <i className={getStatusIcon(mission.status)}></i>
                        <span className="capitalize">{mission.status}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">{mission.title}</h3>
                    <p className="text-gray-400">{mission.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mission Details */}
          <div className="sticky top-8">
            {missions.map((mission) => (
              selectedMission === mission.id && (
                <div key={mission.id} className="bg-gray-800/50 rounded-2xl overflow-hidden border border-gray-700">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={mission.image}
                      alt={mission.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-6 right-6">
                      <h3 className="text-2xl font-bold text-white mb-2">{mission.title}</h3>
                      <p className="text-gray-300">{mission.description}</p>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h4 className="text-lg font-semibold text-white mb-4">Key Milestones</h4>
                    <ul className="space-y-3">
                      {mission.details.map((detail, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <i className="ri-check-line text-blue-400 mt-1 flex-shrink-0"></i>
                          <span className="text-gray-300">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
